/**
 * Cart Header Component
 */

'use client';

import styled from '@emotion/styled';
import { IconButton, Text, Badge } from '@template-project/afrodita-components';
import { ChevronLeft, ShoppingBag } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import {
  CART_HEADER_CLOSE,
  CART_HEADER_ITEMS,
  CART_HEADER_TITLE,
} from './i18.keys';

const Header = styled.div`
  height: 69px;
  width: 100%;
  background: ${({ theme }) => theme.colors.background.primary};
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-content: center;
  padding: ${({ theme }) => theme.space.md};
  box-sizing: border-box;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.color};
`;

const Title = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
`;

const CloseButton = styled(IconButton)`
  padding: ${({ theme }) => theme.space.sm};

  svg {
    pointer-events: none;
  }
`;

const ShoppingBagWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const PositionedItemsBadge = styled(Badge)`
  position: absolute;
  top: -8px;
  right: -8px;
`;

export interface CartHeaderProps {
  itemCount: number;
  onClose: () => void;
}

export function CartHeader({ itemCount, onClose }: CartHeaderProps) {
  const { t } = useTranslation('sidecart');

  return (
    <Header>
      <CloseButton
        variant="ghost"
        testId="close-cart-button"
        onClick={onClose}
        title={t(CART_HEADER_CLOSE)}
        aria-label={t(CART_HEADER_CLOSE)}
        icon={<ChevronLeft size={20} aria-hidden data-testid="close-side-cart" />}
      />

      <Title>
        <Text variant="heading4" as="span">
          {t(CART_HEADER_TITLE)}
        </Text>
        <Text variant="heading4" weight="regular" as="span">
          {t(CART_HEADER_ITEMS, { count: itemCount })}
        </Text>
      </Title>

      <ShoppingBagWrapper>
        <ShoppingBag size={24} aria-hidden />
        {itemCount > 0 && (
          <PositionedItemsBadge
            intent="dark"
            shape="circle"
            size="sm"
            uppercase={false}
          >
            {itemCount}
          </PositionedItemsBadge>
        )}
      </ShoppingBagWrapper>
    </Header>
  );
}
